import type { HubPermissionKey, Role } from "@wwrp/shared";
import {
  IconAlertTriangle,
  IconBulb,
  IconChecklist,
  IconClipboardList,
  IconKey,
  IconLayoutDashboard,
  IconLogs,
  IconServer,
  IconSettings,
  IconShieldLock,
  IconUser,
  IconUsers,
  IconWorld,
} from "@tabler/icons-react";

export interface NavPage {
  id: string;
  to: string;
  label: string;
  description: string;
  keywords: string[];
  icon: React.ElementType;
  end: boolean;
  minRole: Role | null;
  /**
   * When set, nav visibility is driven by the user's resolved permissions map
   * (`user.permissions[permissionKey]`) rather than the static `minRole` check.
   * This means changes made through the hub permissions UI are reflected here
   * without a redeploy.
   */
  permissionKey?: HubPermissionKey;
  /** Visible only to users with the private sysadmin grant. */
  sysadminOnly?: boolean;
  /** Section heading shown above the first item in a new group. Undefined = no heading. */
  section?: string;
}

export const NAV_PAGES: NavPage[] = [
  {
    id: "dashboard",
    to: "/",
    label: "Dashboard",
    description: "Announcements, community stats, and who is online",
    keywords: ["home", "announcements", "overview", "stats", "online"],
    icon: IconLayoutDashboard,
    end: true,
    minRole: null,
  },
  {
    id: "my-profile",
    to: "/my-profile",
    label: "My Profile",
    description: "Your profile, records, and LOA requests",
    keywords: ["me", "profile", "loa", "conduct", "records"],
    icon: IconUser,
    end: false,
    minRole: null,
  },
  {
    id: "settings",
    to: "/settings",
    label: "Settings",
    description: "Manage your account and notification preferences",
    keywords: ["preferences", "notifications", "discord", "account"],
    icon: IconSettings,
    end: false,
    minRole: null,
  },
  {
    id: "my-requests",
    to: "/my-requests",
    label: "My Requests",
    description: "View your LOA and edit request history",
    keywords: ["my", "requests", "loa", "approvals", "history"],
    icon: IconClipboardList,
    end: false,
    minRole: null,
  },
  {
    id: "community",
    to: "/community",
    label: "Community",
    description: "Bulletins, leadership updates, and top playtime leaderboard",
    keywords: ["community", "bulletins", "updates", "playtime", "leaderboard", "priorities"],
    icon: IconWorld,
    end: false,
    minRole: null,
  },
  {
    id: "feedback",
    to: "/feedback",
    label: "Feedback",
    description: "Submit ideas, vote on features, and track the roadmap",
    keywords: ["feedback", "ideas", "feature", "request", "vote", "roadmap", "bugs", "suggestions"],
    icon: IconBulb,
    end: false,
    minRole: null,
  },
  {
    id: "members",
    to: "/members",
    label: "Members",
    description: "Search and view all member profiles",
    keywords: ["search", "list", "staff", "profiles"],
    icon: IconUsers,
    end: false,
    minRole: "staff_in_training" as Role,
    permissionKey: "member.view_list",
    section: "Staff Tools",
  },
  {
    id: "server-players",
    to: "/admin/server",
    label: "Live Players",
    description: "See who is currently online on the FiveM server",
    keywords: ["server", "players", "online", "live", "fivem", "kick", "in-game"],
    icon: IconServer,
    end: false,
    minRole: "staff" as Role,
  },
  {
    id: "compliance",
    to: "/admin/compliance",
    label: "Compliance",
    description: "Review and remediate non-compliant members",
    keywords: ["non-compliant", "audit", "members", "remediation"],
    icon: IconAlertTriangle,
    end: false,
    minRole: "staff" as Role,
    permissionKey: "compliance.view",
  },
  {
    id: "pending-approvals",
    to: "/admin/pending-approvals",
    label: "Pending Approvals",
    description: "Review LOAs and requested updates awaiting approval",
    keywords: ["approvals", "pending", "review", "loa", "requests"],
    icon: IconChecklist,
    end: false,
    minRole: "staff_in_training" as Role,
    permissionKey: "da_update.request",
  },
  {
    id: "audit-log",
    to: "/admin/audit-log",
    label: "Audit Log",
    description: "Review all staff actions across the platform",
    keywords: ["logs", "history", "activity", "actions"],
    icon: IconLogs,
    end: false,
    minRole: "administration" as Role,
    permissionKey: "audit_log.view",
    section: "Administration",
  },
  {
    id: "role-mappings",
    to: "/role-mappings",
    label: "Role Mappings",
    description: "Map Discord roles to hub permission roles",
    keywords: ["discord", "permissions", "roles", "mapping"],
    icon: IconKey,
    end: false,
    minRole: "head_administration" as Role,
    permissionKey: "role_mappings.manage",
  },
  {
    id: "hub-permissions",
    to: "/admin/hub-permissions",
    label: "Hub Permissions",
    description: "Configure minimum role requirements for every hub action",
    keywords: ["permissions", "roles", "access", "control", "configure", "gate"],
    icon: IconShieldLock,
    end: false,
    // Hard-coded private permission — not a configurable role threshold.
    minRole: null,
    sysadminOnly: true,
  },
];
